export type SkuId = string;
export type VendorId = string;
export type EpochDay = number;

export const MS_PER_DAY = 86_400_000;
export const EXCEL_EPOCH_DAY_OFFSET = -25569;
export const UNIX_EPOCH_DAY = 0;

export const toEpochDay = (utcMs: number): EpochDay => Math.floor(utcMs / MS_PER_DAY);
export const fromEpochDay = (d: EpochDay): Date => new Date(d * MS_PER_DAY);
export const epochDayIso = (d: EpochDay): string => fromEpochDay(d).toISOString().slice(0, 10);

export type SourceKind = 'BOM' | 'INVENTORY' | 'SALES' | 'MFG' | 'PURCHASE' | 'PARTS';

export interface Part {
  id: SkuId;
  description: string;
  uom: string;
  productionLeadTimeDays: number | null;
  purchaseLeadTimeDays: number | null;
  safetyStockUnits: number | null;
}

export interface BomEntry {
  parent: SkuId;
  child: SkuId;
  quantityPerParent: number;
  uom: string;
}

export interface InventorySnapshot {
  sku: SkuId;
  onHand: number;
  asOf: EpochDay;
}

export interface PurchaseOrderRow {
  orderNumber: string;
  sku: SkuId;
  vendorId: VendorId | null;
  quantityOutstanding: number;
  expectedDeliveryDate: EpochDay;
}

export interface SalesOrderRow {
  orderNumber: string;
  customerName: string;
  sku: SkuId;
  quantity: number;
  scheduledShipDate: EpochDay;
}

export interface ManufacturingOrderRow {
  workOrderNumber: string;
  sku: SkuId;
  quantity: number;
  scheduledCompletionDate: EpochDay;
}

export interface CanonicalBundle {
  parts: ReadonlyMap<SkuId, Part>;
  bom: readonly BomEntry[];
  inventory: readonly InventorySnapshot[];
  salesOrders: readonly SalesOrderRow[];
  manufacturingOrders: readonly ManufacturingOrderRow[];
  purchaseOrders: readonly PurchaseOrderRow[];
}

export interface Settings {
  today: EpochDay;
  horizonDays: number;
  defaultPurchaseLeadDays: number;
  defaultProductionLeadDays: number;
  safetyStockPercent: number;
}

export type RecommendationConfidence = 'high' | 'medium' | 'low';

export interface PurchaseRecommendation {
  sku: SkuId;
  description: string;
  suggestedQuantity: number;
  uom: string;
  orderByDate: EpochDay;
  neededByDate: EpochDay;
  currentOnHand: number;
  projectedStockoutDate: EpochDay;
  drivingOrders: readonly string[];
  confidence: RecommendationConfidence;
}

export interface StockoutEvent {
  sku: SkuId;
  day: EpochDay;
  gross: number;
  drawn: number;
  deficit: number;
}

export interface SimulationResult {
  recommendations: readonly PurchaseRecommendation[];
  stockoutEvents: readonly StockoutEvent[];
  ranAt: EpochDay;
}

export type SimulationPhase =
  | 'idle'
  | 'expanding-bom'
  | 'seeding-demand'
  | 'simulating'
  | 'done'
  | 'cancelled'
  | 'error';

export interface SimulationProgress {
  phase: SimulationPhase;
  skusProcessed: number;
  skusTotal: number;
}

export interface QuarantineRow {
  source: SourceKind;
  rowIndex: number;
  reason: string;
  raw: Record<string, unknown>;
}

export interface ValidationWarning {
  source: SourceKind;
  message: string;
  count: number;
}
